import { ReactNode, useState } from 'react';

interface CurrentImageListProps<T> {
  items: T[];
  /** Null when no image is current: every item shows, and there is no button. */
  currentTag: string | null;
  tagOf: (item: T) => string;
  /** Returns the item's element with its key. */
  renderItem: (item: T) => ReactNode;
  label: string;
  olderLabel: string;
  /** Shown in place of the current image's items while it has none. */
  noCurrent?: ReactNode;
}

function ItemList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div role="list" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {children}
    </div>
  );
}

/** The items of the current image tag, and the other tags' items behind a button that starts closed. */
export default function CurrentImageList<T>({ items, currentTag, tagOf, renderItem, label, olderLabel, noCurrent }: CurrentImageListProps<T>) {
  const [olderShown, setOlderShown] = useState(false);
  const current = currentTag == null ? items : items.filter((item) => tagOf(item) === currentTag);
  const older = currentTag == null ? [] : items.filter((item) => tagOf(item) !== currentTag);
  return (
    <>
      {current.length > 0 ? <ItemList label={label}>{current.map(renderItem)}</ItemList> : noCurrent}
      {older.length > 0 && (
        <button
          type="button"
          className="btn ghost sm"
          aria-expanded={olderShown}
          onClick={() => setOlderShown(!olderShown)}
          style={{ alignSelf: 'flex-start', height: 28, padding: '0 10px' }}
        >
          {olderShown ? 'Hide older' : `Older (${older.length})`}
        </button>
      )}
      {olderShown && older.length > 0 && <ItemList label={olderLabel}>{older.map(renderItem)}</ItemList>}
    </>
  );
}
